import React, { useState, useEffect } from 'react';
import { Theme, TableConfig } from '../types';
import { CloseIcon, TrashIcon, PlusIcon }
from './icons';

interface GuestListModalProps {
  isOpen: boolean;
  onClose: () => void;
  table: TableConfig | null;
  onUpdateGuestList: (tableId: string, newGuestList: string[]) => void;
  theme: Theme;
}

const GuestListModal: React.FC<GuestListModalProps> = ({ isOpen, onClose, table, onUpdateGuestList, theme }) => {
  const [guestName, setGuestName] = useState('');
  const [currentGuestList, setCurrentGuestList] = useState<string[]>([]);

  useEffect(() => {
    if (table) {
      setCurrentGuestList(table.guestList || []);
    } else {
      setCurrentGuestList([]);
    }
    setGuestName(''); // Reset input when table changes or modal opens/closes
  }, [isOpen, table]);

  if (!isOpen || !table) return null;

  const handleAddGuest = () => {
    if (guestName.trim() === '' || currentGuestList.length >= table.seats) return;
    const updatedList = [...currentGuestList, guestName.trim()];
    setCurrentGuestList(updatedList);
    onUpdateGuestList(table.id, updatedList);
    setGuestName('');
  };

  const handleRemoveGuest = (indexToRemove: number) => {
    const updatedList = currentGuestList.filter((_, index) => index !== indexToRemove);
    setCurrentGuestList(updatedList);
    onUpdateGuestList(table.id, updatedList);
  };
  
  const modalBg = theme === 'dark' ? 'bg-hba-mid-purple' : 'bg-white';
  const textColor = theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-800';
  const secondaryTextColor = theme === 'dark' ? 'text-hba-text-secondary' : 'text-gray-600';
  const titleColor = theme === 'dark' ? 'text-hba-accent-pink' : 'text-hba-accent-purple';
  const inputBgClass = theme === 'dark' ? 'bg-hba-light-purple border-hba-deep-purple text-hba-text-primary' : 'bg-gray-50 border-gray-300 text-gray-900';
  const buttonPrimaryClass = `py-2 px-4 rounded-md font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-opacity-50 ${theme === 'dark' ? 'bg-hba-accent-pink text-white hover:bg-pink-700 focus:ring-hba-accent-pink' : 'bg-hba-accent-purple text-white hover:bg-purple-700 focus:ring-hba-accent-purple'}`;
  const buttonSecondaryClass = `py-2 px-3 rounded-md font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-opacity-50 ${theme === 'dark' ? 'bg-teal-600 text-white hover:bg-teal-500 focus:ring-teal-600' : 'bg-teal-500 text-white hover:bg-teal-600 focus:ring-teal-500'}`;
  const listBg = theme === 'dark' ? 'bg-hba-deep-purple' : 'bg-gray-50';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 transition-opacity duration-300 ease-in-out" onClick={onClose}>
      <div 
        className={`${modalBg} rounded-lg shadow-xl p-6 w-full max-w-md max-h-[85vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-bold ${titleColor}`}>
            Gestionar Invitados: Mesa {table.displayId}
          </h2>
          <button onClick={onClose} className={`p-1 rounded-full ${secondaryTextColor} hover:${textColor} ${theme === 'dark' ? 'hover:bg-hba-light-purple' : 'hover:bg-gray-200'}`}>
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        <p className={`mb-1 text-sm ${secondaryTextColor}`}>
          Capacidad: {table.seats} asientos
        </p>
        <p className={`mb-4 text-sm font-medium ${currentGuestList.length > table.seats ? 'text-red-500' : textColor}`}>
          Invitados en lista: {currentGuestList.length} / {table.seats}
        </p>

        <div className="mb-4 flex space-x-2">
          <input
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Nombre del invitado"
            className={`flex-grow p-2 border rounded-md ${inputBgClass}`}
            disabled={currentGuestList.length >= table.seats}
            onKeyPress={(e) => e.key === 'Enter' && handleAddGuest()}
          />
          <button
            onClick={handleAddGuest}
            className={`${buttonSecondaryClass} flex items-center justify-center`}
            disabled={currentGuestList.length >= table.seats || guestName.trim() === ''}
            aria-label="Añadir invitado"
          >
            <PlusIcon className="w-5 h-5" />
          </button>
        </div>
        
        {currentGuestList.length >= table.seats && (
          <p className="text-xs text-red-500 mb-3 -mt-2">La mesa ha alcanzado su capacidad máxima.</p>
        )}

        <div className={`flex-grow overflow-y-auto border rounded-md p-3 ${listBg} space-y-2`}>
          {currentGuestList.length === 0 ? (
            <p className={`italic ${secondaryTextColor} text-center py-4`}>No hay invitados en esta mesa.</p>
          ) : (
            currentGuestList.map((guest, index) => (
              <div key={index} className={`flex justify-between items-center p-2 rounded ${theme === 'dark' ? 'bg-hba-light-purple/50' : 'bg-gray-100'}`}>
                <span className={`${textColor} text-sm`}>{guest}</span>
                <button
                  onClick={() => handleRemoveGuest(index)}
                  className={`text-red-500 hover:text-red-700 p-1 rounded ${theme === 'dark' ? 'hover:bg-red-600/20' : 'hover:bg-red-100'}`}
                  aria-label={`Eliminar ${guest}`}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="mt-6 text-right">
          <button
            onClick={onClose}
            className={buttonPrimaryClass}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestListModal;